import { useMemo } from "react";
import { bool, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { PriceFormats, SummaryColumns, MinAvgMaxKeys } from "@repo/utils";
import { MAX_VALUES } from "@utils";
import {
  useEstimateStructure,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  ValueCell,
  SummaryWorkTypeBufferTooltip,
  ResetValueButton,
} from "@tools";
import classnames from "classnames";

export const CostCell = observer(({
  element,
  displayCellContent,
  isWorkType,
  ...props
}) => {
  const { t } = useTranslation();
  const { allowEdition, useProfitability } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const className = useVisibilityModeCellClassName(structure.visibility.hideSummaryCost);
  
  const { displayWorkTypeTwoValues, displayAnyValue, usedBuffers, showFixedDecimalPrices } = structure;
  
  const {
    currencyObj,
    roundPrice,
    priceFormat,
    useMinMax,
    maxValuesAsBuffer
  } = structure.settings;
  
  const {
    displayPrice,
    displayPriceMax,
    displayUnbufferedPrice,
    displayUnbufferedPriceMax,
    hasResources,
    // inBreakdown,
    // hasCustomPrice,
    hasFixedPrice,
  } = element;
  
  // const messageProps = useBreakdownValueInputMessage(inBreakdown && isWorkType);
  const showResetButton = displayCellContent && allowEdition && hasFixedPrice;// && usesSummaryPercent;
  
  // const editable = allowEdition && !inBreakdown && !hasResources && !useProfitability;
  const editable = allowEdition && !hasResources && !useProfitability;
  // const editable = allowEdition && !hasResources && !useProfitability; //@todo: check which is right
  
  const showMaxValue = useMemo(() => (
    displayWorkTypeTwoValues[element.workTypeId] && ((editable && !maxValuesAsBuffer) || displayPrice !== displayPriceMax)
  ), [ displayWorkTypeTwoValues, editable, displayPrice, displayPriceMax, maxValuesAsBuffer ]);
  
  const displayUsedBuffers = useMemo(() => (
    isWorkType && !hasFixedPrice && displayCellContent && allowEdition && !useProfitability && displayPrice && structure.hasUsedWorkTypeBuffers(element.id)
  ), [ hasFixedPrice, displayCellContent, displayPrice, allowEdition, useProfitability, isWorkType, usedBuffers ]);
  
  const handleCostChange = (price, isMax) => {
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && useMinMax)
      key = MinAvgMaxKeys.MIN;
    
    element.setFixedPrice({ [key]: price }, true);
  };
  
  const handlePriceReset = () => {
    element.resetFixedPrice(true);
  };
  
  return (
    <ValueCell
      colName={ SummaryColumns.COST }
      allowNegative
      useDynamicWidth
      useInputBackground
      usePriceFormat
      emptyInputs={!displayAnyValue}
      editable={{ min: editable, max: editable && !maxValuesAsBuffer && useMinMax }}
      displayContent={displayCellContent}
      value={displayPrice}
      valueMax={displayPriceMax}
      placeholderMin={displayUnbufferedPrice}
      placeholderMax={displayUnbufferedPriceMax}
      min={-MAX_VALUES.PRICE}
      max={MAX_VALUES.PRICE}
      displayMax={showMaxValue}
      onValueChange={handleCostChange}
      symbolStart={currencyObj.symbolStart}
      symbolEnd={currencyObj.symbolEnd}
      americanFormat={priceFormat === PriceFormats.AMERICAN}
      showDecimals={!roundPrice}
      fixedDecimalScale={showFixedDecimalPrices}
      className={classnames("z-2", className)}
      inputClassName="input-price"
      textPresetClass="preset-price"
      applySuffixSpacing
      inputMinTooltipMessage={
        displayUsedBuffers && !maxValuesAsBuffer &&
        <SummaryWorkTypeBufferTooltip
          workType={element}
          cleanValue={displayUnbufferedPrice}
          isPrice
        />
      }
      inputMaxTooltipMessage={
        displayUsedBuffers &&
        <SummaryWorkTypeBufferTooltip
          workType={element}
          cleanValue={displayUnbufferedPriceMax}
          isMax
          isPrice
        />
      }
      //{ ...messageProps }
      { ...props }
    >
      {
        showResetButton &&
        <ResetValueButton action={handlePriceReset}>
          {t("views.editor.revert_value")}
        </ResetValueButton>
      }
    </ValueCell>
  );
});

CostCell.propTypes = {
  element: object.isRequired,
  displayCellContent: bool,
  extendPadding: bool,
  isWorkType: bool,
};